import {
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { IQuizLatestEntryFormValue } from '../quiz.types';
import { UploadedImage } from './UploadedImage';
import {
  StyledAnswerAgainButton,
  StyledFormCard,
  StyledLastEntryBanner,
} from '../styles';
import {
  StyledAccordionSummaryTitle,
  StyledAlreadyAnsweredNotice,
  StyledAnswerLabel,
  StyledDivider,
  StyledGoHomeWrapper,
  StyledInnerAccordion,
  StyledOuterAccordion,
  StyledOuterAccordionDetails,
} from './styles';

type PreviousAnswersProps = {
  formValues: IQuizLatestEntryFormValue[];
  alreadyAnsweredMessage?: string;
  showGoHome?: boolean;
  onGoHome: () => void;
  creationDate?: string;
};

/**
 * Single card shown when the user has already answered a non-multi quiz.
 * Displays the "already answered" notice, a "go home" button, and the list
 * of previous answers as collapsible accordions.
 */
export function PreviousAnswers({
  formValues,
  alreadyAnsweredMessage,
  showGoHome = false,
  onGoHome,
  creationDate,
}: PreviousAnswersProps): JSX.Element {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <StyledFormCard>
      {creationDate && (
        <StyledLastEntryBanner>
            {t('quiz.lastEntry', {
              date: new Date(creationDate).toLocaleDateString(language),
            })}
        </StyledLastEntryBanner>
      )}
      {/* Already-answered notice + back button */}
      {alreadyAnsweredMessage && (
        <StyledAlreadyAnsweredNotice>
          <Typography variant="body1" color="textSecondary">
            {alreadyAnsweredMessage}
          </Typography>
        </StyledAlreadyAnsweredNotice>
      )}

      {formValues.length > 0 && (
        <React.Fragment>
          <StyledDivider />
          {/* Outer accordion: "Visualiser mes réponses précédentes" */}
          <StyledOuterAccordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="previous-answers-content"
              id="previous-answers-header"
            >
              <StyledAccordionSummaryTitle>
                {t('quiz.previousAnswers.title')}
              </StyledAccordionSummaryTitle>
            </AccordionSummary>
            <StyledOuterAccordionDetails>
              {formValues.map((fv) => {
                const choiceTitles = fv.formInputChoiceValues?.map(
                  (cv) => cv.formInputChoice.title,
                );
                const hasChoices = choiceTitles && choiceTitles.length > 0;
                const imageIri = fv.image ?? null;

                const renderAnswer = () => {
                  if (imageIri) {
                    return <UploadedImage imageIri={imageIri} />;
                  }
                  if (hasChoices) {
                    return (
                      <Typography variant="body2" color="textSecondary">
                        {choiceTitles.join(', ')}
                      </Typography>
                    );
                  }
                  return (
                    <Typography variant="body2" color="textSecondary">
                      {fv.value ?? '–'}
                    </Typography>
                  );
                };

                return (
                  <StyledInnerAccordion
                    key={fv['@id']}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`answer-${fv.id}-content`}
                      id={`answer-${fv.id}-header`}
                    >
                      <StyledAnswerLabel variant="body1">
                        {fv.formInput.label}
                      </StyledAnswerLabel>
                    </AccordionSummary>
                    <AccordionDetails>{renderAnswer()}</AccordionDetails>
                  </StyledInnerAccordion>
                );
              })}
            </StyledOuterAccordionDetails>
          </StyledOuterAccordion>
        </React.Fragment>
      )}

      <StyledGoHomeWrapper>
        {showGoHome && (
          <StyledAnswerAgainButton variant="outlined" onClick={onGoHome}>
            {t('quiz.goBackHome')}
          </StyledAnswerAgainButton>
        )}
      </StyledGoHomeWrapper>
    </StyledFormCard>
  );
}
